"use client";

import React, { useState } from "react";
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import { useDarkMode } from '@/lib/DarkModeContext';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';
import Link from 'next/link';
import { useAuthContext } from '@/lib/AuthContext';
import { db, requireDb } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, query, where, orderBy, onSnapshot } from 'firebase/firestore';

export default function KontaktPage() {
    const { user, loading: authLoading } = useAuthContext();
    const { darkMode } = useDarkMode();
    const { siteLang } = useSiteLanguage();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const [userMessages, setUserMessages] = useState<any[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);

    // Subscribe to messages sent by this user so they can see replies
    React.useEffect(() => {
        if (!user) {
            setUserMessages([]);
            setMessagesLoading(false);
            return;
        }

        setMessagesLoading(true);
        const q = query(collection(requireDb(), 'messages'), where('uid', '==', user.uid), orderBy('createdAt', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            const items: any[] = [];
            snap.forEach((d) => items.push({ id: d.id, ...(d.data() as any) }));
            setUserMessages(items);
            setMessagesLoading(false);
        }, (err) => {
            console.error('user messages snapshot error', err);
            setMessagesLoading(false);
        });

        return () => unsub();
    }, [user]);

    // Auto-fill name and email when user is signed in
    React.useEffect(() => {
        if (user) {
            if (user.displayName) setName(user.displayName);
            if (user.email) setEmail(user.email);
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');

        if (!user) {
            setStatus('error');
            setErrorMsg(siteLang === 'en' ? 'You must be logged in.' : 'Morate biti prijavljeni.');
            return;
        }

        try {
            // Save message to Firestore
            await addDoc(collection(requireDb(), 'messages'), {
                uid: user.uid,
                name,
                email,
                message,
                createdAt: serverTimestamp(),
                read: false
            });

            setStatus('sent');
            setName('');
            setEmail('');
            setMessage('');
        } catch (err: any) {
            console.error('Error sending message:', err);
            setStatus('error');
            const msg = err?.message || (siteLang === 'en' ? 'An error occurred while sending the message.' : 'Došlo je do greške pri slanju poruke.');
            setErrorMsg(msg);
        }
    };

    return (
        <main className="min-h-screen bg-white flex flex-col">
            <Header />

            <section className="mt-0 md:-mt-16 pt-6 md:pt-20 pb-8" style={{ backgroundColor: '#111827' }}>
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">{siteLang === 'en' ? 'Contact' : 'Kontakt'}</h1>
                    <p className="text-base md:text-lg text-gray-300">{siteLang === 'en' ? 'Have a question, collaboration idea, or suggestion? Send a message and we\'ll get back to you.' : 'Imate pitanje, saradnju ili sugestiju? Pošaljite poruku i javit ćemo se.'}</p>
                </div>
            </section>

            <section className="py-12">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="bg-white rounded-2xl shadow-md p-6 md:p-8">
                        {status === 'sent' ? (
                            <div className="text-center">
                                <h2 className="text-2xl font-bold mb-2">{siteLang === 'en' ? 'Thank you!' : 'Hvala!'}</h2>
                                <p className="text-gray-600">{siteLang === 'en' ? 'Your message was sent successfully. We will respond as soon as possible.' : 'Poruka je uspešno poslata. Odgovorićemo u najkraćem roku.'}</p>
                                <Link href="/homepage" className="inline-block mt-6 px-4 py-2 bg-blue-600 text-white rounded-[8px]">{siteLang === 'en' ? 'Back to home' : 'Povratak na početnu'}</Link>
                            </div>
                        ) : (
                            authLoading || !user ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-700 mb-4">{siteLang === 'en' ? <>You must be <strong>logged in</strong> to send a message.</> : <>Morate biti <strong>prijavljeni</strong> da biste poslali poruku.</>}</p>
                                    <div className="flex items-center justify-center gap-3">
                                        <Link href="/login" className="px-4 py-2 bg-blue-600 text-white rounded-[8px]">{siteLang === 'en' ? 'Log in' : 'Prijava'}</Link>
                                        <Link href="/signup" className="px-4 py-2 bg-white text-black rounded-[8px]">{siteLang === 'en' ? 'Register' : 'Registracija'}</Link>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-4">{siteLang === 'en' ? <>Or write to us directly at: <a href="mailto:studijeodbrambenihsistema@gmail.com" className="text-blue-600">studijeodbrambenihsistema@gmail.com</a></> : <>Ili nam pišite direktno na: <a href="mailto:studijeodbrambenihsistema@gmail.com" className="text-blue-600">studijeodbrambenihsistema@gmail.com</a></>}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{siteLang === 'en' ? 'Full name' : 'Ime i prezime'}</label>
                                        <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                        <input required type="email" value={email} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100" />
                                        <p className="text-xs text-gray-500 mt-1">{siteLang === 'en' ? 'Email taken from your account' : 'Email preuzet iz vašeg naloga'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">{siteLang === 'en' ? 'Message' : 'Poruka'}</label>
                                        <textarea required value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <button type="submit" disabled={status === 'sending'} className="px-4 py-2 bg-blue-600 text-white rounded-[8px] hover:bg-blue-700">
                                            {status === 'sending' ? (siteLang === 'en' ? 'Sending...' : 'Šaljem...') : (siteLang === 'en' ? 'Send message' : 'Pošalji poruku')}
                                        </button>
                                        <p className="text-sm text-gray-500">{siteLang === 'en' ? <>Or contact us directly: <a href="mailto:studijeodbrambenihsistema@gmail.com" className="text-blue-600">studijeodbrambenihsistema@gmail.com</a></> : <>Ili nas direktno kontaktirajte: <a href="mailto:studijeodbrambenihsistema@gmail.com" className="text-blue-600">studijeodbrambenihsistema@gmail.com</a></>}</p>
                                    </div>

                                    {status === 'error' && <p className="text-red-600">{errorMsg || (siteLang === 'en' ? 'An error occurred while sending. Please try again.' : 'Došlo je do greške pri slanju. Pokušajte ponovo.')}</p>}
                                </form>
                            )
                        )}

                        {user && (
                            <div className="mt-6 border-t pt-6">
                                <h3 className="text-lg font-semibold mb-3">{siteLang === 'en' ? 'My messages' : 'Moje poruke'}</h3>
                                {messagesLoading ? <p className="text-gray-500">{siteLang === 'en' ? 'Loading...' : 'Učitavanje...'}</p> : userMessages.length === 0 ? <p className="text-gray-500">{siteLang === 'en' ? 'No messages sent.' : 'Nema poslanih poruka.'}</p> : (
                                    <div className="space-y-4">
                                        {userMessages.map((m: any) => (
                                            <div key={m.id} className={`rounded-lg p-3 border ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-900'}`}>
                                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{m.createdAt?.toDate ? new Date(m.createdAt.toDate()).toLocaleString() : ''}</div>
                                                <div className={`mt-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{m.message}</div>
                                                {m.reply && (
                                                    <div className={`${darkMode ? 'mt-3 p-3 bg-gray-700 rounded-lg border border-gray-600' : 'mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100'}`}>
                                                        <div className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-700'}`}>{siteLang === 'en' ? 'Reply:' : 'Odgovor:'}</div>
                                                        <div className={`mt-1 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{m.reply}</div>
                                                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-400'} mt-1`}>{m.replyAt?.toDate ? new Date(m.replyAt.toDate()).toLocaleString() : ''}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <div className="mt-auto">
                <Footer />
            </div>
        </main>
    );
}
